import { useEffect, type FC, useState, Fragment, type UIEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { Typography, Scrollbar, SpinnerLoader } from 'shared/ui'
import { type Segment } from 'entities/Segment/model/Segment'
import { SegmentDropdownCard } from 'entities/Segment'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type SearchDropdownSegmentStore } from 'widgets/SearchDropdown/store/searchDropdownSegmentStore'
import styles from './styles.module.scss'

type ISegmentsDropdownContentItemsProps = {
  store: SearchDropdownSegmentStore
  onChange?: (item: Segment) => void
}

export const SegmentsDropdownContentItems: FC<ISegmentsDropdownContentItemsProps> = observer(
  ({ store, onChange }) => {
    const { loading, hasMore, itemsListIds, loadMore, hasItems, init, options } = store
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const { segmentsStore } = useEntitiesContext()
    const handleScroll = (e: UIEvent) => {
      const target = e.target as HTMLDivElement
      const scrollHeight = target.scrollHeight
      const scrollTop = target.scrollTop
      const offsetHeight = target.offsetHeight

      if (loading) return
      if (!hasMore) return
      if (scrollHeight <= scrollTop + offsetHeight + 100) {
        loadMore()
      }
    }

    const handleSelect = (item: Segment) => {
      onChange?.(item)
      store.selectTypeItem(item)
    }

    const isSelected = (id: number | undefined) => {
      if (!id) return false

      return store.isSelected(id)
    }

    useEffect(() => {
      setSelectedIds(store.selectedItemsListIds)

      if (!loading) {
        init()
      }
    }, [])

    return (
      <Scrollbar autoHeight autoHeightMin={0} autoHeightMax={290} onScroll={handleScroll}>
        <div className={styles.list}>
          {[...selectedIds, ...itemsListIds].map((id, index) => {
            const item = segmentsStore.getItem(id)
            if (!item) return

            return (
              <Fragment key={item.id}>
                <SegmentDropdownCard
                  item={item}
                  InputCheckboxProps={
                    options?.selectedMode
                      ? {
                          checked: isSelected(id),
                          onChecked: () => handleSelect(item),
                        }
                      : undefined
                  }
                  onChange={handleSelect}
                />
                {index === selectedIds.length - 1 && <div className={styles.divider}></div>}
              </Fragment>
            )
          })}
          {!hasItems && !loading && (
            <div className={styles.emptyList}>
              <Typography variant={'body-rg-regular'} ariaLabel={'Not found'}>
                Not found
              </Typography>
            </div>
          )}
          {loading && (
            <div className={styles.spinner}>
              <SpinnerLoader ariaLabel={'SegmentsDropdownContentItems'} />
            </div>
          )}
        </div>
      </Scrollbar>
    )
  }
)
